import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { authService } from './AuthService'
import { STORAGE_USER_KEY } from '../contexts/Auth'
import { messageService } from './MessageService'
import { t } from 'i18next'
import i18n from '../lib/i18n/i18nConfig' 

const apiUrl = window.localStorage.getItem("@idus/cli/api_url") ?? import.meta.env.VITE_API_BASE_URL

const instanceAxios = axios.create({
    timeout: 60000,
    baseURL: apiUrl,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'x-origem': 'WEB'
    }
})

instanceAxios.interceptors.request.use(
    (config) => {
        // Tenta pegar do i18n, se falhar pega do localStorage, se falhar usa pt-BR
        const currentLanguage = i18n.language || window.localStorage.getItem('lang') || 'pt-BR';
        
        // Injeta o header Accept-Language dinamicamente
        config.headers['Accept-Language'] = currentLanguage;
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instanceAxios.interceptors.response.use(
    res => {
        if (res.headers.authorization) {
            authService.setToken(res.headers.authorization)
        }
        return Promise.resolve(res)
    },
    (error: any) => {
        const errorData = error.response?.data || {};
        const errorMsg = errorData.message || errorData.detail;

        if (error.response?.status === 400) {
            messageService.errorWithDetail(t("apiService.messages.badRequest"), errorMsg || "Bad Request")
            return Promise.reject(error.response)
        }

        if (error.response?.status === 401) {
            authService.logout()
            localStorage.removeItem(STORAGE_USER_KEY)
            messageService.errorWithDetail(t("apiService.messages.unauthorized.title"), t("apiService.messages.unauthorized.content"), () => {
                window.location.href = '/'
            })
        }

        if (error.response?.status === 403) {
            messageService.errorWithDetail(t("apiService.messages.forbidden"), errorMsg || "")
        }

        if (error.response?.status === 404) {
            if (errorMsg) {
                messageService.errorWithDetail(t("apiService.messages.notFound"), errorMsg)
                return Promise.reject(errorMsg)
            }
            throw new Error('Não encontrado.')
        }

        if (error.response?.status === 409) {
            if (errorMsg) {
                messageService.errorWithDetail(t("apiService.messages.conflict"), errorMsg)
                return Promise.reject(errorMsg)
            }

            throw new Error("Não foi possível salvar a informação, pois houve conflito de informações.")
        }

        if (error.response?.status === 500) {
            const msg = t("apiService.messages.internalServerErrSupport")
            
            if (errorData.validation) {
                 return Promise.reject(errorData.validation)
            }

            if (errorMsg) {
                messageService.errorWithDetail(t("apiService.messages.internalServerErr"), errorMsg)
                return Promise.reject(errorMsg)
            }
            
            messageService.error(msg)
            return Promise.reject(msg)
        }

        if (!error.response) {
            const mensagem = t("apiService.messages.internalServerErrNoConnection")
            messageService.error(mensagem)
            return Promise.reject(mensagem)
        }
        throw error
    }
)

class ApiAxios {
    setToken(token: string | null): void {
        instanceAxios.defaults.headers.Authorization = `${token}`
    }

    getToken(): string {
        return '' + instanceAxios.defaults.headers.Authorization
    }

    get(url: string): Promise<AxiosResponse> {
        return instanceAxios.get(url)
    }

    post(url: string, data: any, options = {}): Promise<AxiosResponse> {
        return instanceAxios.post(url, data, options)
    }

    postFile(url: string, data: any, config?: Omit<AxiosRequestConfig, 'headers' | 'data'>): Promise<AxiosResponse> {
        return instanceAxios.post(url, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            ...config
        })
    }
    postFileJson(url: string, data: any): Promise<AxiosResponse> {
        return instanceAxios.post(url, data, {
            responseType: 'blob' as 'json'
        })
    }
    patchFile(url: string, data: any): Promise<AxiosResponse> {
        return instanceAxios.patch(url, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
        })
    }

    getFile(url: string): Promise<AxiosResponse> {
        return instanceAxios.get(url, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            responseType: 'blob' as 'json'
        })
    }

    patch(url: string, data: any): Promise<AxiosResponse> {
        return instanceAxios.patch(url, data)
    }

    delete(url: string): Promise<AxiosResponse> {
        return instanceAxios.delete(url)
    }
}

export const ApiService = new ApiAxios()